/**
 * Portal shell - owner: ui-shell. One approved shell for all three surfaces (QM-D02-003 / QM-NC02-001), laid out to the
 * Quixo OS dashboard design (owner, 8 Oct 2026): a light rail with the product mark, grouped navigation and a help card;
 * a top bar with the search field (/), the command palette (⌘K / Ctrl+K: jump to any screen, recent screens first),
 * theme toggle, notifications bell, EN/AR switch and the account menu (settings, security, notifications, help, sign
 * out); breadcrumbs (area > page > detail) above every page; the workspace on the soft ground. Brand kit tokens
 * unmodified, data-brand="quixo-marketing", Inter Tight.
 * Responsive drawer (QM-D02-001 / QM-NC02-002): persistent at >= 1024 px, off-canvas dialog below that, opening from the
 * inline-start edge so it comes from the right in Arabic. EN/AR RTL (QM-D08-001), dark/light/system + reduced motion
 * (QM-NC02-003), notifications bell (QM-D14-001), workspace switcher + global search (QM-D02-004), cross-tab sign-out and
 * device purge (QM-D19-003).
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { BrandRoot, IconButton, SkipLink, VisuallyHidden } from '@qm/ui';
import { assets } from '@qm/brand';
import { createCrossTabSignOut, SESSION_BROADCAST_CHANNEL, type BroadcastLike } from '@qm/contracts/identity';
import { AppProvider, loadSession, useApp, useOptionalApp } from '../lib/app-context';
import { bff, setSessionObserver } from '../lib/bff';
import { browserDeviceApis, clearDeviceData } from '../lib/device';
import { AUTH_NEXT_KEY, isAuthPath, isPublicPath, safeNext, signInUrl, signOutUrl } from '../lib/deeplink';
import { dialogKeydown, FOCUSABLE } from '../lib/focus';
import { applyDocumentPrefs, resolveMotion, resolveTheme, type ResolvedTheme } from '../lib/prefs';
import { resetResourceCache } from '../lib/resource';
import { Icon, type GlyphName } from './Icon';
import { SupportAssistant } from './SupportAssistant';
import { CommandPalette } from './CommandPalette';
import { IdleWarning } from './IdleWarning';
import { ViewAsBanner } from './ViewAs';
import { PastDueBanner } from './PastDueBanner';
import { entryFor, isActive, navFor } from './nav';
import { SurfaceNavProvider, useSurfaceNavItems, type SurfaceNavItem } from './surface-nav';
import { KNOWLEDGE_HAS_SOURCES, useFlags, useKnowledgeHasSources } from '../lib/flags';

export type ShellSurface = 'customer' | 'partner' | 'admin';

/** Surfaces on the 2026 calm-workspace re-skin (redesign.css, keyed on data-ui="2026"). Rolled out one surface per PR. */
export const REDESIGNED_SURFACES: ReadonlySet<ShellSurface> = new Set<ShellSurface>(['customer', 'partner', 'admin']);
export const uiVersion = (surface: ShellSurface): '2026' | undefined => (REDESIGNED_SURFACES.has(surface) ? '2026' : undefined);

export function Shell({ surface }: { surface: ShellSurface }) {
  const existing = useOptionalApp();
  const frame = <ShellFrame surface={surface}><Outlet /></ShellFrame>;
  return existing ? frame : <AppProvider>{frame}</AppProvider>;
}

export function useMedia(query: string): boolean {
  const [on, setOn] = useState(() => typeof matchMedia === 'function' && matchMedia(query).matches);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const m = matchMedia(query);
    const fn = () => setOn(m.matches);
    m.addEventListener('change', fn);
    return () => m.removeEventListener('change', fn);
  }, [query]);
  return on;
}

/**
 * The menu registry wraps the whole frame: the frame itself reads the partner/staff menu (useMenu), so the provider
 * must sit above it. Inside the frame's own markup the read happened outside the provider and always saw [], which
 * left the partner portal and staff console with only "Home".
 */
export function ShellFrame({ surface, children }: { surface: ShellSurface; children: ReactNode }) {
  return <SurfaceNavProvider><Frame surface={surface}>{children}</Frame></SurfaceNavProvider>;
}

function Frame({ surface, children }: { surface: ShellSurface; children: ReactNode }) {
  const app = useApp();
  const { t, locale, dir, ui, me } = app;
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useCollapsedRail();
  const [palette, setPalette] = useState(false);
  const menu = useMenu(surface);
  const systemDark = useMedia('(prefers-color-scheme: dark)');
  const systemReduce = useMedia('(prefers-reduced-motion: reduce)');
  const theme = resolveTheme(ui.theme, systemDark);
  const motion = resolveMotion(ui.motion, systemReduce);

  // <html lang dir data-theme data-motion> follow preferences.
  useEffect(() => {
    if (typeof document !== 'undefined') applyDocumentPrefs(document.documentElement, { locale, theme, motion });
  }, [locale, theme, motion]);

  // Session: hydrate once; any 401 with x-qm-session-state signs out every tab and purges device data.
  const hydrate = app.hydrate;
  useEffect(() => {
    let live = true;
    // No session (anonymous 204, or 401) -> this surface's /signin, returning here after. Public pages stay.
    const onUnauthorized = () => {
      if (!live || isPublicPath(surface, window.location.pathname)) return;
      window.location.assign(signInUrl(window.location.pathname, window.location.search));
    };
    loadSession({ onUnauthorized }).then((s) => { if (live && s) hydrate(s); });
    let channel: BroadcastLike & { close?: () => void } | null = null;
    try { channel = typeof BroadcastChannel === 'function' ? (new BroadcastChannel(SESSION_BROADCAST_CHANNEL) as unknown as BroadcastLike & { close(): void }) : null; } catch { channel = null; }
    if (channel) {
      const guard = createCrossTabSignOut(channel, () => {
        void clearDeviceData(browserDeviceApis(resetResourceCache), 'session_expired').then(() => {
          if (!isPublicPath(surface, window.location.pathname)) window.location.assign(signOutUrl(surface, window.location.pathname, window.location.search));
        });
      });
      setSessionObserver(guard.observe);
    }
    return () => { live = false; setSessionObserver(null); channel?.close?.(); };
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Cmd/Ctrl+K opens the command palette on every surface (the search field keeps "/").
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'k' || !(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      e.preventDefault();
      setPalette((v) => !v);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // After Entra (lands on /) or a magic-link return, resume the page the user was trying to open.
  useEffect(() => {
    if (!me || (surface === 'admin' && !me.roles.some((r) => r.startsWith('staff_')))) return;
    const landing = location.pathname === '/' || isAuthPath(location.pathname);
    if (!landing) {
      try { sessionStorage.removeItem(AUTH_NEXT_KEY); } catch { /* storage blocked */ }
      return;
    }
    let stored: string | null;
    try { stored = sessionStorage.getItem(AUTH_NEXT_KEY); } catch { return; }
    if (!stored) return;
    const dest = safeNext(stored);
    const here = `${location.pathname}${location.search}`;
    try { sessionStorage.removeItem(AUTH_NEXT_KEY); } catch { /* storage blocked */ }
    if (dest === '/' || dest === location.pathname || dest === here) return;
    window.location.assign(dest);
  }, [me, surface, location.pathname, location.search]);

  const lockup = theme === 'dark' ? assets.lockupOnBlack : assets.lockup;
  return (
    <BrandRoot dir={dir} lang={locale}>
      <div className="qm-shell" data-surface={surface} data-ui={uiVersion(surface)} data-drawer={open ? 'open' : 'closed'} data-rail={collapsed ? 'collapsed' : 'full'}>
        <SkipLink>{t('shell.skipToContent')}</SkipLink>
        <header className="qm-shell__bar">
          <IconButton className="qm-shell__menu" label={open ? t('shell.menu.close') : t('shell.menu.open')} aria-expanded={open} aria-controls="qm-drawer" onClick={() => setOpen((v) => !v)}>
            <Icon name={open ? 'close' : 'menu'} />
          </IconButton>
          <Link to="/" className="qm-shell__brand" aria-label={t('shell.home')}>
            <img className="qm-shell__lockup" src={lockup} alt={t('common.brand')} width={168} height={28} />
          </Link>
          {surface === 'customer' && me?.tenantId ? <GlobalSearch /> : null}
          <div className="qm-shell__actions">
            {me ? (
              <button type="button" className="qm-shell__jump" aria-haspopup="dialog" aria-expanded={palette} onClick={() => setPalette(true)} title={t('shell.palette.title')}>
                <Icon name="sparkle" size={16} /><span>{t('shell.palette.open')}</span><kbd className="qm-shell__kbd" aria-hidden="true">⌘ K</kbd>
              </button>
            ) : null}
            <WorkspaceSwitcher />
            <ThemeToggle theme={theme} />
            {me ? <NotificationBell /> : null}
            <LanguageToggle />
            {me ? <AccountMenu surface={surface} email={me.email} onSignOut={() => void app.signOut()} /> : null}
            {me ? (
              <IconButton className="qm-shell__signout" label={t('shell.signOut')} onClick={() => void app.signOut()}><Icon name="logout" /></IconButton>
            ) : null}
          </div>
        </header>
        <div className="qm-shell__body">
          <Drawer surface={surface} menu={menu} open={open} onClose={() => setOpen(false)} current={location.pathname} collapsed={collapsed} onCollapse={() => setCollapsed(!collapsed)} />
          <main id="main" className="qm-shell__main" tabIndex={-1}>
            {me && surface !== 'admin' ? <ViewAsBanner surface={surface} /> : null}
            {me && surface === 'customer' ? <PastDueBanner /> : null}
            <IdleWarning />
            <Breadcrumbs menu={menu} current={location.pathname} />
            {children}
          </main>
        </div>
        <footer className="qm-shell__footer"><p>{t('common.poweredBy')}</p></footer>
        <BottomNav menu={menu} current={location.pathname} onMore={() => setOpen(true)} />
        {me ? <CommandPalette surface={surface} menu={menu} open={palette} onClose={() => setPalette(false)} /> : null}
        {me && !isAuthPath(location.pathname) ? <SupportAssistant key={surface + ":" + (me.tenantId ?? "") + ":" + me.email} surface={surface} /> : null}
      </div>
    </BrandRoot>
  );
}

function Drawer({ surface, menu, open, onClose, current, collapsed, onCollapse }: { surface: ShellSurface; menu: readonly SurfaceNavItem[]; open: boolean; onClose: () => void; current: string; collapsed: boolean; onCollapse: () => void }) {
  const { t, me } = useApp();
  const sections = groupMenu(menu);
  const activeTo = entryFor(menu, current)?.to;
  const [collapsedSections, toggleSection] = useCollapsedSections();
  const ref = useRef<HTMLElement>(null);
  const opener = useRef<Element | null>(null);
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    opener.current = document.activeElement;
    const el = ref.current;
    const items = () => Array.from(el?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    items()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      const list = items();
      const r = dialogKeydown(e, list.indexOf(document.activeElement as HTMLElement), list.length);
      if (r === 'close') onClose();
      else if (typeof r === 'number') list[r]?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); (opener.current as HTMLElement | null)?.focus?.(); };
  }, [open, onClose]);
  const workspace = me?.tenantId ? (me.memberships ?? []).find((m) => m.tenantId === me.tenantId)?.tenantName ?? null : null;
  return (
    <>
      <div className="qm-drawer__scrim" hidden={!open} onClick={onClose} aria-hidden="true" />
      <nav id="qm-drawer" ref={ref} className="qm-drawer" aria-label={t('shell.nav.label')} data-open={open ? 'true' : 'false'}
        {...(open ? { role: 'dialog', 'aria-modal': true } : {})}>
        <div className="qm-drawer__top">
          <Link to="/" className="qm-drawer__brand" aria-label={t('shell.home')} tabIndex={-1}>
            <img className="qm-drawer__emblem" src={assets.emblem} alt="" width={36} height={36} />
            <span className="qm-drawer__product"><b>{t('shell.product.name')}</b><small>{t('shell.product.tagline')}</small></span>
          </Link>
          <button type="button" className="qm-drawer__collapse" onClick={onCollapse} aria-pressed={collapsed} aria-label={collapsed ? t('shell.rail.expand') : t('shell.rail.collapse')} title={collapsed ? t('shell.rail.expand') : t('shell.rail.collapse')}>
            <Icon name={collapsed ? 'expand' : 'collapse'} size={18} />
          </button>
        </div>
        <ul className="qm-drawer__list">
          {sections.map((sec, si) => {
            const hasCurrent = sec.items.some((n) => n.to === activeTo);
            const open = !sec.heading || hasCurrent || !collapsedSections.has(sec.heading);
            const hid = `qm-nav-${si}`;
            return (
              <li key={sec.heading || 'top'} className="qm-drawer__sectioned">
                {sec.heading ? (
                  <button type="button" id={hid} className="qm-drawer__section" aria-expanded={open} aria-controls={`${hid}-list`} onClick={() => toggleSection(sec.heading)}>
                    <span>{sec.heading}</span><Icon name="chevron" size={14} />
                  </button>
                ) : null}
                <ul id={`${hid}-list`} className="qm-drawer__sublist" aria-labelledby={sec.heading ? hid : undefined} hidden={!open}>
                  {sec.items.map((n) => {
                    const active = n.to === activeTo;
                    const props = { className: 'qm-drawer__link', title: n.label, 'aria-current': active ? 'page' as const : undefined, 'data-active': active ? 'true' : undefined, 'data-testid': n.testId };
                    const body = <><Icon name={n.icon} /><span>{n.label}</span></>;
                    return (
                      <li key={n.to}>
                        {n.document ? <a href={n.to} {...props}>{body}</a> : <Link to={n.to} {...props}>{body}</Link>}
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
        {/* below 640 px the switcher lives here instead of the top bar (QM-D02-004) */}
        <div className="qm-drawer__switcher"><WorkspaceSwitcher /></div>
        <div className="qm-drawer__foot">
          <div className="qm-drawer__help">
            <span className="qm-drawer__help-art" aria-hidden="true"><Icon name="headset" size={20} /></span>
            <b>{t('shell.help.title')}</b>
            <small>{t('shell.help.body')}</small>
            <Link to={surface === 'customer' ? '/help' : '/'} className="qm-button" data-variant="ink" data-size="sm">{t('shell.help.cta')}</Link>
          </div>
          {workspace ? (
            <p className="qm-drawer__workspace"><Icon name="shield" size={14} /><span><b>{workspace}</b><small>{t('shell.rail.isolated')}</small></span></p>
          ) : null}
          {typeof window !== 'undefined' ? <p className="qm-drawer__domain">{window.location.host}</p> : null}
        </div>
      </nav>
    </>
  );
}

/**
 * The surface's menu: customer entries from shell/nav (flag-aware), partner/admin entries registered by the surface.
 * Computed once per frame and shared by the rail, the phone tab bar, the breadcrumbs and the command palette.
 */
export function useMenu(surface: ShellSurface): readonly SurfaceNavItem[] {
  const { t } = useApp();
  // only customer nav items carry flags; partner/admin surfaces never fetch them
  const flag = useFlags(surface === 'customer');
  const hasKnowledge = useKnowledgeHasSources(flag, surface === 'customer');
  const surfaceItems = useSurfaceNavItems();
  if (surface !== 'customer' && surfaceItems.length) return surfaceItems;
  return navFor(surface, (k) => flag(k) === 'on', (c) => c === KNOWLEDGE_HAS_SOURCES && hasKnowledge).map((n) => ({
    to: n.path, label: t(n.labelKey), icon: n.icon, ...(n.group ? { group: t(n.group) } : {}), ...(n.tab ? { tab: true } : {}), ...(n.document ? { document: true } : {}),
  }));
}

/** Home > area > page (> detail). The page itself is plain text with aria-current; nothing renders on the home page. */
function Breadcrumbs({ menu, current }: { menu: readonly SurfaceNavItem[]; current: string }) {
  const { t } = useApp();
  if (current === '/') return null;
  const entry = entryFor(menu, current);
  if (!entry || entry.to === '/') return null;
  const detail = current !== entry.to;
  return (
    <nav className="qm-crumbs" aria-label={t('shell.crumbs.label')}>
      <ol>
        <li><Link to="/">{t('shell.home')}</Link></li>
        {entry.group ? <li><span>{entry.group}</span></li> : null}
        <li>{detail ? (entry.document ? <a href={entry.to}>{entry.label}</a> : <Link to={entry.to}>{entry.label}</Link>) : <span aria-current="page">{entry.label}</span>}</li>
        {detail ? <li><span aria-current="page">{t('shell.crumbs.detail')}</span></li> : null}
      </ol>
    </nav>
  );
}

/** Consecutive items sharing a heading form one category; items with no heading sit on top. */
export function groupMenu(items: readonly SurfaceNavItem[]): { heading: string; items: SurfaceNavItem[] }[] {
  const out: { heading: string; items: SurfaceNavItem[] }[] = [];
  for (const n of items) {
    const heading = n.group ?? '';
    const last = out.at(-1);
    if (last && last.heading === heading) last.items.push(n);
    else out.push({ heading, items: [n] });
  }
  return out;
}

const SECTIONS_KEY = 'qm.nav.collapsed';
/** Remembers which categories the person folded away (per device). The category holding the current page always stays open. */
function useCollapsedSections(): [ReadonlySet<string>, (heading: string) => void] {
  const [set, setSet] = useState<ReadonlySet<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(SECTIONS_KEY) ?? '[]') as string[]); } catch { return new Set(); }
  });
  const toggle = (heading: string) => {
    const next = new Set(set);
    if (next.has(heading)) next.delete(heading); else next.add(heading);
    setSet(next);
    try { localStorage.setItem(SECTIONS_KEY, JSON.stringify([...next])); } catch { /* storage blocked */ }
  };
  return [set, toggle];
}

export function LanguageToggle() {
  const { locale, setLocale, t } = useApp();
  return (
    <div className="qm-shell__lang" role="group" aria-label={t('shell.language.switch')}>
      {(['en', 'ar'] as const).map((l) => (
        <button key={l} type="button" lang={l} aria-pressed={locale === l} data-active={locale === l ? 'true' : undefined} onClick={() => { if (locale !== l) void setLocale(l); }}>
          {t(`shell.language.${l}`)}
        </button>
      ))}
    </div>
  );
}

/** Light / dark switch (QM-NC02-003). Stores the explicit choice; "match my device" stays available in Settings. */
export function ThemeToggle({ theme }: { theme: ResolvedTheme }) {
  const { t, setUi } = useApp();
  const next: ResolvedTheme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button type="button" className="qm-shell__theme" role="switch" aria-checked={theme === 'dark'} aria-label={t('shell.theme.toggle')} title={t(`shell.theme.${next}`)} onClick={() => void setUi({ theme: next })}>
      <span className="qm-shell__theme-icon" data-on={theme === 'light' ? 'true' : undefined}><Icon name="sun" size={16} /></span>
      <span className="qm-shell__theme-icon" data-on={theme === 'dark' ? 'true' : undefined}><Icon name="moon" size={16} /></span>
    </button>
  );
}

/** Where the account menu leads on each surface (customer: account pages; partner: security; staff: sign out only). */
const ACCOUNT_LINKS: Record<ShellSurface, readonly { to: string; key: string; icon: GlyphName }[]> = {
  customer: [
    { to: '/settings', key: 'shell.nav.accountSettings', icon: 'settings' },
    { to: '/settings/security', key: 'shell.screens.security.title', icon: 'key' },
    { to: '/notifications', key: 'shell.accountMenu.notifications', icon: 'bell' },
    { to: '/help', key: 'shell.nav.help', icon: 'help' },
  ],
  partner: [{ to: '/security', key: 'shell.screens.security.title', icon: 'key' }],
  admin: [],
};

/** Avatar chip that discloses the account menu (Escape or a click outside closes it; focus returns to the chip). */
function AccountMenu({ surface, email: rawEmail, onSignOut }: { surface: ShellSurface; email: string; onSignOut: () => void }) {
  const email = rawEmail ?? '';
  const { t, me } = useApp();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); button.current?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const local = email.split('@')[0]!;
  const initials = local.split(/[._-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('') || '?';
  const name = local.split(/[._-]+/).filter(Boolean).map((p) => p[0]!.toUpperCase() + p.slice(1)).join(' ') || email;
  const role = me?.roles[0];
  const roleLabel = role && ROLE_KEYS.has(role) ? t(`shell.role.${role}`) : (me?.memberships ?? []).find((m) => m.tenantId === me?.tenantId)?.tenantName ?? '';
  return (
    <div className="qm-shell__account-wrap" ref={wrap}>
      <button type="button" ref={button} className="qm-shell__account" title={email} aria-label={`${t('shell.account')}: ${email}`} aria-expanded={open} aria-controls="qm-account-menu" onClick={() => setOpen((v) => !v)}>
        <span className="qm-shell__avatar" aria-hidden="true">{initials}</span>
        <span className="qm-shell__who" aria-hidden="true"><b>{name}</b>{roleLabel ? <small>{roleLabel}</small> : null}</span>
        <span className="qm-shell__who-chev" aria-hidden="true"><Icon name="chevron" size={14} /></span>
      </button>
      <div id="qm-account-menu" className="qm-shell__account-menu" role="group" aria-label={t('shell.account')} hidden={!open}>
        <p className="qm-shell__account-email"><bdi>{email}</bdi></p>
        {ACCOUNT_LINKS[surface].map((l) => (
          <Link key={l.to} to={l.to} className="qm-shell__account-item"><Icon name={l.icon} size={16} /><span>{t(l.key)}</span></Link>
        ))}
        <button type="button" className="qm-shell__account-item" onClick={onSignOut}><Icon name="logout" size={16} /><span>{t('shell.signOut')}</span></button>
      </div>
    </div>
  );
}
const ROLE_KEYS = new Set(['customer_owner', 'customer_admin', 'customer_member', 'customer_viewer', 'partner_owner', 'partner_admin', 'partner_member', 'staff_admin', 'staff_ops', 'staff_support', 'staff_finance', 'staff_readonly', 'staff_content']);

const RAIL_KEY = 'qm.rail.collapsed';
function useCollapsedRail(): [boolean, (v: boolean) => void] {
  const [v, setV] = useState(() => { try { return localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; } });
  return [v, (next) => { setV(next); try { localStorage.setItem(RAIL_KEY, next ? '1' : '0'); } catch { /* storage blocked */ } }];
}

/** Phone tab bar: the four most-used destinations plus "More", which opens the full menu. Hidden from 1024 px. */
function BottomNav({ menu, current, onMore }: { menu: readonly SurfaceNavItem[]; current: string; onMore: () => void }) {
  const { t } = useApp();
  const all = menu.filter((n) => !n.document);
  const tabs = all.filter((n) => n.tab);
  const items = (tabs.length ? tabs : all).slice(0, 4);
  if (!items.length) return null;
  return (
    <nav className="qm-bottomnav" aria-label={t('shell.nav.primary')}>
      {items.map((n) => {
        const active = isActive(n.to, current);
        return (
          <Link key={n.to} to={n.to} className="qm-bottomnav__item" aria-current={active ? 'page' : undefined} data-active={active ? 'true' : undefined}>
            <Icon name={n.icon} /><span>{n.label}</span>
          </Link>
        );
      })}
      <button type="button" className="qm-bottomnav__item" onClick={onMore}><Icon name="menu" /><span>{t('shell.nav.more')}</span></button>
    </nav>
  );
}

function NotificationBell() {
  const { t, me } = useApp();
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!me?.tenantId) return;
    let live = true;
    const load = () => bff<{ count: number }>('/v1/notifications/unread-count').then((r) => { if (live) setCount(r.count); }, () => undefined);
    void load();
    const id = setInterval(load, 60_000);
    return () => { live = false; clearInterval(id); };
  }, [me?.tenantId]);
  return (
    <Link to="/notifications" className="qm-shell__bell" aria-label={t('notifications.bell', { count })}>
      <Icon name="bell" />
      {count > 0 ? <span className="qm-shell__count" aria-hidden="true">{count > 99 ? '99+' : count}</span> : null}
    </Link>
  );
}

function WorkspaceSwitcher() {
  const { me, t, switchWorkspace } = useApp();
  const names = new Map<string, string>();
  for (const m of me?.memberships ?? []) if (m.status === 'active' && m.tenantId && !names.has(m.tenantId)) names.set(m.tenantId, m.tenantName || m.tenantId);
  if (!me || names.size < 2) return null;
  return (
    <label className="qm-shell__switcher">
      <VisuallyHidden>{t('shell.workspace.switch')}</VisuallyHidden>
      <select className="qm-input" value={me.tenantId ?? ''} onChange={(e) => void switchWorkspace(e.target.value)}>
        {[...names].map(([id, name]) => <option key={id} value={id}>{name}</option>)}
      </select>
    </label>
  );
}

function GlobalSearch() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const slash = e.key === '/' && !e.metaKey && !e.ctrlKey && !el?.closest('input, textarea, select, [contenteditable="true"]');
      if (!slash) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (q.trim().length >= 2) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };
  return (
    <form role="search" className="qm-shell__search" onSubmit={submit}>
      <label htmlFor="qm-search" className="qm-sr-only">{t('shell.search.label')}</label>
      <Icon name="search" />
      <input id="qm-search" ref={inputRef} className="qm-input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('shell.search.placeholder')} minLength={2} maxLength={100} />
      <kbd className="qm-shell__kbd" aria-hidden="true" title={t('shell.search.shortcut')}>/</kbd>
    </form>
  );
}
